import { Moment } from '@e3dc-react/shell/Libs/moment'
import { UnitTypes } from '../types'
import { DateSettings } from '../system-data-handler/types'
import { LoadingState } from '../chart-kit'
import { ZoomIndexType } from '../system-charts/shared'
import type { ChartEventHandlers } from '../chart-kit/shared/chart-event-handlers'

export type Timestamp = { ts: number }

/* Generic type for data points with price, validFrom and validTo properties. */
export interface PriceItem {
  price: number | null
  validFrom: string
  validTo: string
}

export interface PowerPrice extends PriceItem {
  price: number
}

export type NullablePowerPrice = PriceItem

export type FeedInPrice = PriceItem

/* Generic type for data points with time and price properties. */
export interface ChartPriceItem {
  time: number
  price: number | null
}

export interface ChartValues {
  hour: string
  from: string
  to: string
  price: number | null
  isEmptyPoint?: boolean
}

export interface AreaDot {
  cx: number
  cy: number
  index: number
  payload?: ChartValues
}
export interface CostSavingChartData {
  time: number | string
  consumptionPrice: number | null
  threeSixtyWallboxProducedEnergy: number | null
  threeSixtyWallboxSavings: number | null
  threeSixtyBatteryProducedEnergy: number | null
  threeSixtyBatterySavings: number | null
}

export interface CostSavingChartExportData extends CostSavingChartData {
  batteryMode: EmsBatteryMode | null
}

export type CsvData = Record<keyof CostSavingChartExportData, string>
export type CostSavingChartDataWithDynamicEms = CostSavingChartData & {
  [key in EmsBatteryMode]?: number
} & {
  batteryMode?: EmsBatteryMode
}
export interface DynamicEmsData {
  start: string
  end: string
  emsBatteryMode: EmsBatteryMode
  emsSgReadyMode: EmsSgReadyMode | null
}

export enum EmsBatteryMode {
  GridCharge = 'GRID_CHARGE',
  BlockDischarging = 'BLOCK_DISCHARGING',
  NormalOperation = 'NORMAL_OPERATION',
  PvChargingDelayed = 'PV_CHARGING_DELAYED',
  PvChargingReduced = 'PV_CHARGING_REDUCED',
}
export interface EmsSgReadyStatus {
  state: EmsSgReadyMode | null
  disabled: boolean
}

export enum EmsSgReadyMode {
  // ! in case of changing any value here, also update the sgReadyModes keys inside `packages/theme/{e3dc,flow}/palette.ts`
  Blocked = 'BLOCKED',
  Normal = 'NORMAL',
  Recommended = 'RECOMMENDED',
  Forced = 'FORCED',
}

export type UseCheckDynamicPowerPriceApplicability = {
  isDynamicPowerPriceEnabledForWallboxes: boolean
  isDynamicPowerPriceEnabledForBattery: boolean
  isDynamicPowerPriceEnabled: boolean
  isDynamicPowerPriceLoading: boolean
}

export type DynamicPowerPricesEnabledContextProps = UseCheckDynamicPowerPriceApplicability

export interface TooltipValues {
  price: string
  from: string
  to: string
}

export interface ThreeSixtySavings {
  time: Moment
  netOutWh: number | null
  savings: number | null
  weightedPrice?: number | null
}

export type DynamicPricesDto = {
  gridfee?: number
  surcharge?: number
  furthertaxes?: number
  dppEnabledWb?: boolean
  priceLimitWb?: number
  dppEnabledBattery?: boolean
  enableDynamicEms?: boolean
  priceLimitBattery?: number
  activeMonthsBattery?: number[]
  maxSocBattery?: number
}

export type DynamicPricesModel = {
  networkFees: string
  energyPriceSurcharge: string
  taxes: string
  dynamicPricesForWallboxesEnabled: boolean
  priceLimitWallbox: string
  dynamicPricesForBatteryEnabled?: boolean
  isDynamicEmsEnabledForBattery?: boolean /** can only be true if dynamicPricesForBatteryEnabled is also true */
  priceLimitBattery?: string
  activeMonthsBattery?: number[]
  batteryStateOfChargeLimit?: string
}

export enum DynamicPriceType {
  Battery = 0,
  Wallbox = 1,
}

export enum FeedInTariffType {
  Fixed = 'FIXED',
  ZeroWhenNegativePrices = 'ZERO_WHEN_NEGATIVE_PRICES',
}

export type DynamicEmsSettingsDto = {
  battery?: boolean
  sgReady?: boolean
  feedInTariffType: `${FeedInTariffType}` | null
  isActive: boolean
}

export type UpdateDynamicEmsSettingsDto = {
  battery?: boolean
  sgReady?: boolean
  feedInTariffType?: `${FeedInTariffType}` | null
}

export type DynamicEmsSettingsModel = {
  isDynamicEmsEnabledForBattery?: boolean
  isSgReadyEnabled?: boolean
  feedInTariffType: `${FeedInTariffType}` | null
  isActive: boolean
}

export type UpdateDynamicEmsSettingsModel = {
  isDynamicEmsEnabledForBattery?: boolean
  isSgReadyEnabled?: boolean
  feedInTariffType?: `${FeedInTariffType}` | null
}

export type DynamicEmsDataDto = {
  start: string
  end: string
  emsBatteryMode: EmsBatteryMode
  emsSgReadyMode: EmsSgReadyMode | null
}

export type DynamicEmsDataModel = {
  start: string
  end: string
  emsBatteryMode: EmsBatteryMode
  emsSgReadyMode: EmsSgReadyMode | null
}

export type ReferencePriceModel = {
  referencePrice?: string
}
export interface ReferencePriceDto {
  id: number[]
  serialno: number[]
  from: number[]
  to: number[]
  price: number[]
  createdAt: string[]
}
export interface ReferencePricesPostDto {
  prices: ReferencePrice[]
}

export interface ReferencePrice {
  from: number | null
  price: string | number
}

export enum EnergyDirection {
  Charge = 'charge',
}

export type EnergyDirections = `${EnergyDirection}`

export type DynamicPricesSettings = DynamicPricesModel &
  ReferencePriceModel &
  Pick<DynamicEmsSettingsModel, 'isDynamicEmsEnabledForBattery' | 'isSgReadyEnabled'>

/** Form-defaults for price-fallback */
export const PRICE_FALLBACK_THREE_DEC = '0.000'
export const PRICE_FALLBACK_TWO_DEC = '0.00'
export const PRICE_FALLBACK_NO_DEC = '0'

/** Form values for the dpp-widget (only wallbox and battery priceLimit) */
export type DynamicPricesSettingsForm = Pick<DynamicPricesSettings, 'priceLimitWallbox' | 'priceLimitBattery'>

export type GeneralFormFields = {
  titleKey: string
  descriptionKey: string
  fieldName: string
  fieldSuffix: UnitTypes
  isDecimal: boolean
  step: number
  precision: number
}

export type BatterySettingsField = {
  key: string
  titleKey?: string
  titleIcon?: React.ReactNode
  descriptionKey: string
  suffix?: React.ReactNode
}

export interface CostSavingsChartsProps {
  dateSettings: DateSettings
  data: CostSavingChartData[]
  loadingState: LoadingState
  showLegend: boolean
  chartEventHandlers?: ChartEventHandlers
  zoomStartIndex?: ZoomIndexType
  zoomEndIndex?: ZoomIndexType
  isZoomedIn?: boolean
  zoomedFrom?: string | number | null
  zoomedTo?: string | number | null
}

export type DppBatteryStatus = 'disabled' | 'enabledWithDynamicEms' | 'enabled'

export type ThreeSixtySavingsResolution = 'hour' | 'day' | 'month'
